<script setup lang="ts">
/**
 * SectionAnchorNav
 *
 * In-page jump navigation styled as a horizontal text-tab bar (Telstra-style):
 * light text tabs over a full-width bottom border, with the active section
 * underlined via scroll-spy. Smooth-scrolls to anchor IDs within the page.
 *
 * Interaction: quiet wayfinding, not a primary CTA.
 */

interface AnchorItem {
  label: string;
  targetId?: string; // scroll target: a block's anchorId slug (e.g. "pricing") or fallback block-<id>
  href?: string; // OR an intent link (e.g. /contact?need=...) that opens the quote modal
}

interface Props {
  title?: string;
  subtitle?: string;
  items: AnchorItem[];
  sectionBg?: "white" | "neutral";
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  subtitle: "",
  sectionBg: "white",
});

const HEADER_OFFSET = 150; // sticky site header (84px) + this sticky nav bar

const activeId = ref("");
let observer: IntersectionObserver | null = null;

const { handleLink } = useIntentModal();

function scrollToAnchor(targetId: string) {
  const element = document.getElementById(targetId);
  if (element) {
    const topPos =
      element.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top: topPos, behavior: "smooth" });
  }
}

// An item is either a scroll target (targetId) or an intent link (href, e.g.
// /contact?need=...) that opens the quote modal, like the final-cta button.
function onItem(item: AnchorItem) {
  if (item.href) {
    if (handleLink(item.href)) return;
    navigateTo(item.href);
    return;
  }
  if (item.targetId) scrollToAnchor(item.targetId);
}

// Scroll-spy: highlight the section currently below the header.
onMounted(() => {
  const els = props.items
    .map((i) => (i.targetId ? document.getElementById(i.targetId) : null))
    .filter((el): el is HTMLElement => el !== null);
  if (!els.length) return;
  activeId.value = els[0]!.id;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) activeId.value = entry.target.id;
      }
    },
    { rootMargin: "-100px 0px -60% 0px", threshold: 0 }
  );
  els.forEach((el) => observer!.observe(el));
});

onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
  <section :class="[sectionBg === 'neutral' ? 'bg-neutral-50' : 'bg-white']">
    <!-- Optional header (used by hub pages that set a title) -->
    <div
      v-if="title || subtitle"
      class="mx-auto max-w-7xl px-4 pt-14 sm:px-6 sm:pt-20 lg:px-8"
    >
      <h2
        v-if="title"
        class="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
      >
        {{ title }}
      </h2>
      <p v-if="subtitle" class="mt-3 text-lg leading-7 text-neutral-600">
        {{ subtitle }}
      </p>
    </div>

    <!-- Tab bar with full-width bottom border. Stickiness is applied to the
         block wrapper by BlockRenderer (when data.sticky) so it can follow the
         page; a sticky element here would be trapped inside this short section. -->
    <div
      class="border-b border-neutral-300 shadow-sm"
      :class="[
        sectionBg === 'neutral' ? 'bg-neutral-50' : 'bg-white',
        title || subtitle ? 'mt-6' : '',
      ]"
    >
      <nav
        class="mx-auto flex max-w-7xl flex-wrap gap-x-8 px-4 sm:px-6 lg:px-8"
        aria-label="Page sections"
      >
        <button
          v-for="item in items"
          :key="item.label"
          type="button"
          :aria-current="item.targetId && activeId === item.targetId ? 'true' : undefined"
          :class="[
            '-mb-px border-b-2 py-4 text-[15px] font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
            item.targetId && activeId === item.targetId
              ? 'border-primary-500 text-slate-900'
              : 'border-transparent text-neutral-600 hover:text-slate-900',
          ]"
          @click="onItem(item)"
        >
          {{ item.label }}
        </button>
      </nav>
    </div>
  </section>
</template>
